import React, { createContext, useEffect, useLayoutEffect, useState } from 'react';
import {Navigate, useLocation, useParams} from 'react-router-dom';
import CaseHeader from 'app/edozvola/cases/header/case-header';
import { IRootState, useAppDispatch } from 'app/config/store';
import { fetchCase } from 'app/shared/reducers/case-entity';
import SidebarWrapper from 'app/shared/layout/sidebar/sidebar-wrapper';
import { useSelector } from 'react-redux';
import { SidebarItemPair } from 'app/shared/layout/sidebar/configuration/sidebar-configuration';
import { FullScreenLoader } from 'app/shared/loader/fullscreen-loader';
import { CasesGuard } from 'app/edozvola/cases/cases-guard';

export const CaseTabContext = createContext({ sidebarItemPair: null, setSidebarItemPair: null });

const CaseFormWrapper = ({ children }) => {
  const { id } = useParams<{ id: string }>();
  const appDispatch = useAppDispatch();
  const path = useLocation();
  const currentCaseTabPath = path.pathname.split('/').pop();
  const [sidebarItemPair, setSidebarItemPair] = useState<SidebarItemPair>(null);
  const [fetchingCaseFinished, setFetchingCaseFinished] = useState<boolean>(false);

  const selectedCase = useSelector((state: IRootState) => state.caseEntity);

  useLayoutEffect(() => {
    appDispatch(fetchCase(id));
  }, []);

  useEffect(() => {
    if (selectedCase?.caseTabs && selectedCase?.caseTabs.length > 0) {
      setFetchingCaseFinished(false);
      setSidebarItemPair(findTabByPath(currentCaseTabPath) ?? { sidebarItemKey: null, readonly: true });
      setTimeout(() => setFetchingCaseFinished(true));
    }
  }, [selectedCase]);

  const findFirstTab = () => {
    let firstCaseTab = null;

    selectedCase?.caseTabs.find(caseTab => {
      if (caseTab.sidebarItemPath === null) {
        caseTab.children.find(childTab => {
          if (childTab.sidebarItemPath !== null && firstCaseTab === null) {
            firstCaseTab = childTab;
            return true;
          }
          return false;
        });
      } else if (firstCaseTab === null) {
        firstCaseTab = caseTab;
        return true;
      }
      return false;
    });

    return firstCaseTab;
  };

  const findTabByPath = (path: string) => {
    let firstCaseTab = null;

    selectedCase?.caseTabs.find(caseTab => {
      if (caseTab.sidebarItemPath === null) {
        caseTab.children.find(childTab => {
          if (childTab.sidebarItemPath !== null && firstCaseTab === null && childTab.sidebarItemPath === path) {
            firstCaseTab = childTab;
            return true;
          }
          return false;
        });
      } else if (firstCaseTab === null && caseTab.sidebarItemPath === path) {
        firstCaseTab = caseTab;
        return true;
      }
      return false;
    });

    return firstCaseTab;
  }

  return (
    <>
      {!fetchingCaseFinished && <FullScreenLoader />}
      {fetchingCaseFinished &&
        (children ? (
          location.pathname.includes('/case-submission') ? (
            <CaseTabContext.Provider value={{ sidebarItemPair, setSidebarItemPair }}>
              <SidebarWrapper
                webId={selectedCase?.webId}
                navigationItems={selectedCase?.caseTabs?.map(item => ({ ...item, collapsed: true }))}
                returnUrl={path.state?.from}
                relativePath={true}
              >
                <CaseHeader />
                {children}
              </SidebarWrapper>
            </CaseTabContext.Provider>
          ) : (
            <CasesGuard>
              <CaseTabContext.Provider value={{ sidebarItemPair, setSidebarItemPair }}>
                <SidebarWrapper
                  webId={selectedCase?.webId}
                  navigationItems={selectedCase?.caseTabs?.map(item => ({ ...item, collapsed: true }))}
                  returnUrl={path.state?.from}
                  relativePath={true}
                >
                  <CaseHeader />
                  {children}
                </SidebarWrapper>
              </CaseTabContext.Provider>
            </CasesGuard>
          )
        ) : selectedCase?.caseTabs && selectedCase?.caseTabs.length > 0 ? (
          <Navigate replace to={findFirstTab().sidebarItemPath} state={{ from: path.state?.from }} />
        ) : (
          <Navigate replace to={'/case-submission'} />
        ))}
    </>
  );
};

export default CaseFormWrapper;
